<template>
  <div class="event-banner-container">
    <!-- Active/Past Event Banner -->
    <router-link v-if="hasEvent" :to="{ name: 'events' }" class="banner-image"
      :class="{ 'is-current-page': isOnEventsPage }">
      <img :src="bannerImageUrl" :alt="bannerTitle" />

      <!-- Event Info positioned in center -->
      <div class="event-info">
        <div class="event-title">
          {{ bannerTitle }}
        </div>
        <div v-if="bannerDescription" class="event-description">
          {{ bannerDescription }}
        </div>
        <div class="event-timer">
          <i v-if="hasActiveEvent" class="fa-solid fa-clock"></i>
          <i v-else class="fa-solid fa-flag-checkered"></i>
          <span>{{ timeRemainingText }}</span>
        </div>
        <button v-if="!isOnEventsPage" :disabled="isOnEventsPage" class="cta-button">
          <i class="fa-solid fa-trophy"></i>
          {{ t('EVENT_BANNER.viewEvent') }}
        </button>

        <!-- Fancy scroll text for events page -->
        <div v-else class="scroll-indicator" @click="scrollDown">
          <div class="scroll-text">
            <span class="scroll-label">{{ t('EVENT_BANNER.scrollForDetails') }}</span>
            <div class="scroll-arrow">
              <i class="fa-solid fa-chevron-down"></i>
            </div>
          </div>
        </div>
      </div>
    </router-link>

    <!-- Empty State (No Events) -->
    <div v-else class="banner-image banner-empty">
      <img :src="defaultBannerUrl" alt="SkinWhales" />
      <div class="event-info">
        <img :src="logoUrl" alt="SkinWhales" class="logo-passive" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { usePromotionalEventsStore } from '@/store/promotionalEvents'
import { useI18n } from 'vue-i18n'
import defaultBannerUrl from '@/assets/banners/1.webp'
import logoUrl from '@/assets/SKINWHALES.svg'

const route = useRoute()
const eventsStore = usePromotionalEventsStore()
const { hasActiveEvent, hasEvent, currentEvent, timeRemaining } = storeToRefs(eventsStore)
const { t } = useI18n()

let timeUpdateInterval: ReturnType<typeof setInterval>

const isOnEventsPage = computed(() => route.name === 'events')

const bannerImageUrl = computed(() => {
  if (hasEvent.value && currentEvent.value?.bannerUrl) {
    return currentEvent.value.bannerUrl
  }
  return defaultBannerUrl
})

const bannerTitle = computed(() => {
  if (hasEvent.value && currentEvent.value?.name) {
    return currentEvent.value.name
  }
  return ''
})

const bannerDescription = computed(() => {
  if (hasEvent.value && currentEvent.value?.description) {
    return currentEvent.value.description
  }
  return null
})

const timeRemainingText = computed(() => {
  // Past event - show ended message
  if (!hasActiveEvent.value) {
    return t('EVENT_BANNER.eventEnded')
  }

  // Active event - show time remaining
  if (!timeRemaining.value) return t('EVENT_BANNER.eventEnded')

  const totalSeconds = Math.floor(timeRemaining.value / 1000)
  const days = Math.floor(totalSeconds / (24 * 60 * 60))
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else {
    return `${minutes}m`
  }
})

onMounted(() => {
  // Update timer every minute
  timeUpdateInterval = setInterval(() => {
    // Timer will update reactively through computed
  }, 60000)
})

onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval)
  }
})

const scrollDown = async (event) => {
  event.preventDefault()
  event.stopPropagation()
  
  await nextTick()
  const earningGuideElement = document.querySelector('.earning-guide') as HTMLElement
  if (earningGuideElement) {
    const elementTop = earningGuideElement.offsetTop
    const offset = 80 // Add 80px margin from top
    window.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth'
    })
  }
}
</script>

<style scoped lang="scss">
@use 'sass:color';

.event-banner-container {
  margin-bottom: 24px;

  .banner-image {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    display: block;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
    aspect-ratio: 3.74 / 1;
    position: relative;

    &:not(.is-current-page):hover {
      opacity: 0.98;

      .cta-button:not(:disabled) {
        background: color.scale($green-button-bg, $lightness: 10%);
        border-color: color.scale($accent-lime, $lightness: 10%);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
      }
    }

    &.is-current-page {
      cursor: default;
    }

    &.banner-empty {
      cursor: default;
      pointer-events: none;
    }

    @include mobile {
      border-radius: 0;
    }

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
      object-position: center;
      display: block;
      position: absolute;
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(ellipse at center,
          rgb(0 0 0 / 30%) 0%,
          rgb(0 0 0 / 0%) 50%,
          transparent 80%);
      pointer-events: none;
      z-index: 1;
    }

    .event-info {
      position: relative;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
      width: 100%;
      padding: 0 1rem;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 12px;

      .logo-passive {
        height: auto;
        width: 100%;
        max-height: 80px;
        display: block;
        margin: 0 auto;
        object-fit: contain;
        filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.8));

        @include mobile {
          max-height: 60px;
        }
      }

      .event-title {
        font-size: 48px;
        font-weight: 800;
        user-select: none;

        @include mobile {
          font-size: 28px;
          margin-bottom: 0;
        }
      }

      .event-description {
        font-size: 1rem;
        margin-bottom: 1rem;
        line-height: 1.4;
        user-select: none;
        max-width: 50%;
        text-align: center;

        @include mobile {
          display: none;
        }
      }

      .event-timer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 1rem;
        font-weight: 600;
        user-select: none;

        @include mobile {
          display: none;
        }

        i {
          color: rgba(255, 255, 255, 0.7);
        }
      }
    }

    .cta-button {
      z-index: 2;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      height: 3rem;
      margin-top: 1rem;
      background: $green-button-bg;
      border: 2px solid $accent-lime;
      border-radius: 8px;
      color: white;
      text-decoration: none;
      font-weight: 700;
      font-size: 16px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      user-select: none;
      cursor: pointer;

      i {
        font-size: 16px;
      }

      &:disabled {
        filter: grayscale(100%);
        opacity: 0.5;
        cursor: not-allowed;
      }

      @include tablet {
        display: none;
      }
    }

    .scroll-indicator {
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 3rem;
      margin-top: 1rem;
      user-select: none;
      cursor: pointer;

      .scroll-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        animation: bounce 2s ease-in-out;

        .scroll-label {
          font-size: 14px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          letter-spacing: 0.5px;
        }

        .scroll-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;

          i {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.8);
            animation: pulse 1.5s ease-in-out;
          }
        }

        &:hover .scroll-arrow {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
          transform: scale(1.1);
        }
      }

      @include tablet {
        display: none;
      }
    }@keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-8px);
      }
      60% {
        transform: translateY(-4px);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 0.8;
        transform: scale(1);
      }
      50% {
        opacity: 1;
        transform: scale(1.1);
      }
    }
  }

  @include mobile {
    .banner-image {
      .event-info {
        .event-title {
          font-size: 24px;
        }

        .event-description {
          font-size: 14px;
        }

        .event-timer {
          font-size: 12px;
        }
      }
    }
  }
}
</style>
